import { EmailIcon, Icon } from '@chakra-ui/icons'
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Center,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  HStack,
  keyframes,
  Stack,
  StackProps,
  Text,
  useBreakpointValue
} from '@chakra-ui/react'
import CustomModalOverlay from 'components/common/Modal/CustomModalOverlay'
import ExternalLink from 'components/links/ExternalLink'
import MedleveLogo from 'components/svg/MedleveLogo'
import OmensLogoType from 'components/svg/OmensLogoType'
import WhatsApp from 'components/svg/WhatsApp'
import { Body3, H1 } from 'components/typography'
import {
  MEDLEVE_SUPPORT_EMAIL,
  MEDLEVE_WHATSAPP_LINK,
  MEDLEVE_WHATSAPP_NUMBER_MASKED,
  OMENS_SUPPORT_EMAIL,
  OMENS_WHATSAPP_LINK,
  OMENS_WHATSAPP_NUMBER_MASKED
} from 'constants/omensSupportContact'
import {
  patientAreaIconMap,
  PatientAreaPage,
  patientAreaPageTitleMap
} from 'hooks/app/patientArea/common/usePatientAreaPageTitle'
import { useIsIshaPage } from 'hooks/helpers/useIsIshaPage'
import { useIsMedlevePage } from 'hooks/helpers/useIsMedlevePage'
import Cookies from 'js-cookie'
import {
  ISHA_SUPPORT_EMAIL,
  ISHA_WHATSAPP_LINK,
  ISHA_WHATSAPP_NUMBER_MASKED
} from 'layouts/LadingLayout/components/Footer/IshaFooter'
import { isEmpty, upperFirst } from 'lodash'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { FC, PropsWithChildren, ReactElement, useCallback, useEffect, useMemo } from 'react'
import { BsPower, BsShieldFillCheck } from 'react-icons/bs'
import { FaGraduationCap, FaStethoscope } from 'react-icons/fa'
import { signOut } from 'redux/slices/auth-slice'
import { onSidebarClose, resetPatientAreaUiState } from 'redux/slices/patient-area-ui-slice'
import { useAppDispatch, useAppSelector } from 'redux/store'
import storage from 'redux/store/persist-storage'

export const PATIENT_AREA_SIDEBAR_WIDTH = 320
interface PatientAreaSidebarProps {
  isIsha?: boolean
}
const PatientAreaSidebar: FC<PatientAreaSidebarProps> = ({ isIsha }) => {
  const dispatch = useAppDispatch()
  const profile = useAppSelector((state) => state.auth.profile)
  const isSidebarOpen = useAppSelector((state) => state.patientAreaUi.isSidebarOpen)
  const isMobile = useBreakpointValue({ base: true, lg: false })
  const packageSubscriptions = useAppSelector((state) => state.subscriptions.packageSubscriptions)
  const consultations = useAppSelector((state) => state.consultations.consultations)
  const isMedleve = useIsMedlevePage()

  // Show a "live" tag next to Consultas ONLY while a live VIDEO consultation is
  // open — `isLive` is exclusively the live waiting-line video flow (chat/phone/
  // scheduled never set it), and waiting|started means the call is still open.
  // Lets the patient find their way back if they navigate away during the call.
  const hasOpenLiveConsultation = useMemo(
    () => consultations.some((c) => c.isLive && (c.state === 'waiting' || c.state === 'started')),
    [consultations]
  )
  const onClose = useCallback(() => {
    dispatch(onSidebarClose())
  }, [dispatch])

  const handleLogout = useCallback(() => {
    // Limpa todos os cookies antes de fazer logout
    const cookies = Cookies.get()
    Object.keys(cookies).forEach((cookieName) => {
      Cookies.remove(cookieName, { path: '/' })
      Cookies.remove(cookieName, { path: '/', domain: '.omens.com.br' })
    })
    dispatch(resetPatientAreaUiState())
    dispatch(signOut())

    storage.removeItem('persist:root')
  }, [dispatch])

  const sidebarAccordions = useMemo(() => {
    if (!profile) return null

    const isAcademyOnly = profile.isAcademy && !profile.isConsultation

    const academyAccordion = (
      <SidebarAccordion
        label="Cursos"
        icon={<Icon as={FaGraduationCap} />}
        items={[PatientAreaPage.PROGRAM_TIME, PatientAreaPage.PROGRAM_MASTER, PatientAreaPage.HELP]}
      />
    )

    if (isAcademyOnly) return academyAccordion
    const isBlocked = isMedleve || isIsha
    return (
      <>
        <SidebarAccordion
          label="Espaço pessoal"
          icon={<Icon as={FaStethoscope} />}
          items={
            [
              !isMedleve && PatientAreaPage.NEW_CONSULTATION,
              !isMedleve && PatientAreaPage.MEDICAL_INFO,
              isMedleve && PatientAreaPage.MY_EVOLUTION,
              isMedleve && PatientAreaPage.MEDLEVE_DOCUMENTS,
              profile?.isWeight && !isIsha && PatientAreaPage.WEIGHT_DOCUMENTS,
              profile?.isWeight && isIsha && PatientAreaPage.ISHA_DOCUMENTS,
              PatientAreaPage.DOCUMENTS,
              PatientAreaPage.ORDERS,
              (!!profile.subscriptions?.length || !!packageSubscriptions?.length) && PatientAreaPage.SUBSCRIPTIONS,
              PatientAreaPage.MY_CREDIT_CARDS,
              !!profile.canSeePrepSidebar && PatientAreaPage.PREP_DOCUMENTS,
              !isBlocked && PatientAreaPage.PHYTOTHERAPY
            ].filter(Boolean) as PatientAreaPage[]
          }
        />

        {!isBlocked && academyAccordion}
      </>
    )
  }, [isIsha, isMedleve, packageSubscriptions?.length, profile])

  const sidebarContent = useMemo(
    () => (
      <Stack overflow="auto" px={6} spacing={4}>
        <SidebarItem
          label={patientAreaPageTitleMap[PatientAreaPage.HOME]}
          icon={patientAreaIconMap[PatientAreaPage.HOME]}
          path={PatientAreaPage.HOME}
        />

        <SidebarItem
          label={patientAreaPageTitleMap[PatientAreaPage.CONSULTATIONS]}
          icon={patientAreaIconMap[PatientAreaPage.CONSULTATIONS]}
          path={!isEmpty(consultations) ? PatientAreaPage.CONSULTATIONS : PatientAreaPage.NEW_CONSULTATION}
        >
          {hasOpenLiveConsultation ? <LiveTag /> : undefined}
        </SidebarItem>

        {profile?.isWeight && (
          <SidebarItem
            label={patientAreaPageTitleMap[PatientAreaPage.WEIGHT_DASHBOARD]}
            icon={patientAreaIconMap[PatientAreaPage.WEIGHT_DASHBOARD]}
            path={PatientAreaPage.WEIGHT_DASHBOARD}
          />
        )}
        {isMedleve && (
          <SidebarItem
            label={patientAreaPageTitleMap[PatientAreaPage.NEW_CONSULTATION]}
            icon={patientAreaIconMap[PatientAreaPage.NEW_CONSULTATION]}
            path={PatientAreaPage.NEW_CONSULTATION}
          />
        )}

        {isMedleve && (
          <Link href={PatientAreaPage.GLP1} onClick={() => dispatch(onSidebarClose())}>
            <Text fontSize="lg" color="gold" fontWeight="400" pl="26px" py={1}>
              Combater os efeitos colaterais GLP1
            </Text>
          </Link>
        )}

        {isMedleve && (
          <SidebarItem
            label={patientAreaPageTitleMap[PatientAreaPage.MEDICAL_INFO]}
            icon={patientAreaIconMap[PatientAreaPage.MEDICAL_INFO]}
            path={PatientAreaPage.MEDICAL_INFO}
          />
        )}

        {!profile?.isWeight && profile?.gender !== 'female' && !isMedleve && (
          <SidebarItem
            label={patientAreaPageTitleMap[PatientAreaPage.OMENS_FIT]}
            icon={patientAreaIconMap[PatientAreaPage.OMENS_FIT]}
            path={PatientAreaPage.OMENS_FIT}
          >
            <NewTag />
          </SidebarItem>
        )}

        {sidebarAccordions}

        <SidebarItem
          label={patientAreaPageTitleMap[PatientAreaPage.ACCOUNT]}
          icon={patientAreaIconMap[PatientAreaPage.ACCOUNT]}
          path={PatientAreaPage.ACCOUNT}
        />

        {!isMedleve && (
          <SidebarItem
            label={patientAreaPageTitleMap[PatientAreaPage.SHARE_MY_COUPON]}
            icon={patientAreaIconMap[PatientAreaPage.SHARE_MY_COUPON]}
            path={PatientAreaPage.SHARE_MY_COUPON}
          />
        )}

        <SidebarItem label="Sair" icon={<Icon as={BsPower} fontSize="xl" color="alabaster" />} onClick={handleLogout} />
      </Stack>
    ),
    [
      consultations,
      handleLogout,
      hasOpenLiveConsultation,
      isMedleve,
      profile?.gender,
      profile?.isWeight,
      sidebarAccordions
    ]
  )

  if (isMobile) {
    return (
      <Drawer
        isOpen={isSidebarOpen}
        onClose={onClose}
        placement="left"
        size={{
          base: 'full',
          sm: 'sm'
        }}
      >
        <CustomModalOverlay />
        <DrawerContent
          bg={isIsha ? 'ishafit.70' : isMedleve ? 'medleve' : 'cyan.90'}
          color={isIsha ? 'ishafit.20' : 'white'}
          p="4"
        >
          <DrawerHeader>
            <HStack justify="space-between">
              {isMedleve ? (
                <MedleveLogo h={{ base: '80px', lg: '100px' }} w={{ base: '80px', lg: '100px' }} />
              ) : (
                <OmensLogoType color="dandelion" />
              )}
              <DrawerCloseButton color="alabaster" fontSize="16px" pos="static" />
            </HStack>
          </DrawerHeader>

          <DrawerBody p={0}>
            <Stack spacing={8}>
              <Stack spacing={6} p={6} pb={0}>
                <WhatsappBlock />
              </Stack>
              {sidebarContent}
            </Stack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Stack
      bg={isIsha ? 'ishafit.70' : isMedleve ? 'medleve' : 'cyan.90'}
      color={isIsha ? 'ishafit.20' : 'white'}
      spacing={4}
      maxH="100vh"
      minW={PATIENT_AREA_SIDEBAR_WIDTH}
      maxW={PATIENT_AREA_SIDEBAR_WIDTH}
    >
      <Stack spacing={6} px={6}>
        <WhatsappBlock />
      </Stack>

      {sidebarContent}
    </Stack>
  )
}

export default PatientAreaSidebar

const WhatsappBlock = () => {
  const isIsha = useIsIshaPage()
  const isMedleve = useIsMedlevePage()
  return (
    <Stack minH={{ lg: '89px' }} justify="center">
      <ExternalLink
        href={isIsha ? ISHA_WHATSAPP_LINK : isMedleve ? MEDLEVE_WHATSAPP_LINK : OMENS_WHATSAPP_LINK}
        color="alabaster"
        display={{ base: 'none', lg: 'block' }}
      >
        <HStack>
          <WhatsApp />
          <Text color="alabaster">
            {isIsha
              ? ISHA_WHATSAPP_NUMBER_MASKED
              : isMedleve
              ? MEDLEVE_WHATSAPP_NUMBER_MASKED
              : OMENS_WHATSAPP_NUMBER_MASKED}
          </Text>
        </HStack>
      </ExternalLink>
      <ExternalLink
        href={isIsha ? ISHA_WHATSAPP_LINK : isMedleve ? MEDLEVE_WHATSAPP_LINK : OMENS_WHATSAPP_LINK}
        color="alabaster"
        display={{ base: 'block', lg: 'none' }}
      >
        <HStack>
          <WhatsApp />
          <Text>Atendimento</Text>
        </HStack>
      </ExternalLink>
      <ExternalLink
        href={`mailto:${isIsha ? ISHA_SUPPORT_EMAIL : isMedleve ? MEDLEVE_SUPPORT_EMAIL : OMENS_SUPPORT_EMAIL}`}
        color="alabaster"
      >
        <HStack>
          <EmailIcon boxSize={5} />
          <Text color="alabaster">
            {isIsha ? ISHA_SUPPORT_EMAIL : isMedleve ? MEDLEVE_SUPPORT_EMAIL : OMENS_SUPPORT_EMAIL}
          </Text>
        </HStack>
      </ExternalLink>
    </Stack>
  )
}

const UserBlock = () => {
  const profile = useAppSelector((state) => state.auth.profile)

  if (!profile) return null

  return (
    <HStack justify="space-between" spacing={8}>
      <Stack>
        <H1>{upperFirst(profile.firstName)}</H1>
        <H1>{upperFirst(profile.lastName)}</H1>
      </Stack>

      <Icon as={BsShieldFillCheck} fontSize="4xl" />
    </HStack>
  )
}

interface SidebarItemProps extends StackProps {
  label: string
  icon: ReactElement | null
  path?: string
  children?: ReactElement
}

const SidebarItem: FC<SidebarItemProps> = ({ label, icon, path, children, ...props }) => {
  const router = useRouter()
  const dispatch = useAppDispatch()

  const isActive = useMemo(() => !!path && router.pathname.includes(path), [path, router.pathname])

  const Wrapper: FC<PropsWithChildren> = ({ children }) => {
    if (path)
      return (
        <Link href={path} onClick={() => dispatch(onSidebarClose())}>
          {children}
        </Link>
      )

    return (
      <Box cursor="pointer" role="button">
        {children}
      </Box>
    )
  }

  return (
    <Wrapper>
      <HStack py={1} {...props}>
        <Center w="18px">{icon}</Center>
        <Stack w="full" direction="row" justify="space-between">
          <Text fontSize="lg" color="alabaster" fontWeight={isActive ? '800' : '400'}>
            {label}
          </Text>
          {children}
        </Stack>
      </HStack>
    </Wrapper>
  )
}

interface SidebarAccordionProps {
  label: string
  icon: ReactElement | null
  items: PatientAreaPage[]
}

const SidebarAccordion: FC<SidebarAccordionProps> = ({ label, icon, items }) => {
  const router = useRouter()
  const isActive = useMemo(() => items.some((path) => router.pathname.includes(path)), [items, router.pathname])

  return (
    <Accordion allowToggle defaultIndex={isActive ? 0 : undefined}>
      <AccordionItem border="none">
        <AccordionButton
          w="full"
          rounded="md"
          pl={0}
          py={0}
          color="alabaster"
          _focus={{
            shadow: 'blue'
          }}
        >
          <HStack justify="space-between" w="full" py={1}>
            <HStack>
              <Center w="18px">{icon}</Center>
              <Text fontSize="lg" color="alabaster" fontWeight={isActive ? '800' : '400'}>
                {label}
              </Text>
            </HStack>

            <AccordionIcon />
          </HStack>
        </AccordionButton>

        <AccordionPanel>
          <Stack>
            {items.map((path) => (
              <SidebarItem
                key={path}
                label={patientAreaPageTitleMap[path]}
                icon={patientAreaIconMap[path]}
                path={path}
              />
            ))}
          </Stack>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  )
}

const NewTag: FC = () => {
  const blink = keyframes`
  0% { opacity: 1 }
  50% { opacity: .1 }
  100% { opacity: 1 }
`
  return (
    <Stack justify="center" bg="greenfit.30" px={2} rounded="12px">
      <Body3 color="greenfit.100" fontWeight={800} animation={`${blink} 2s linear infinite`}>
        NEW!
      </Body3>
    </Stack>
  )
}

const LiveTag: FC = () => {
  const blink = keyframes`
  0% { opacity: 1 }
  50% { opacity: .2 }
  100% { opacity: 1 }
`
  return (
    <HStack spacing="6px" align="center" bg="red.500" px={2} py="2px" rounded="full">
      <Box w="7px" h="7px" rounded="full" bg="white" animation={`${blink} 1.2s ease-in-out infinite`} />
      <Body3 color="white" fontWeight={800} fontSize="10px" letterSpacing="0.04em">
        AO VIVO
      </Body3>
    </HStack>
  )
}
